import { useCallback, useMemo, useState } from "react"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import {
    FetchIndustryCodesResultResponse,
    createIndustryCode,
    deleteIndustryCodeItem,
    fetchIndustryCodesResult,
    POSQuery,
} from "../SupplierAdminPOSApi"
import { MRT_ColumnDef } from "material-react-table"
import { IndustryCode, IndustryCodeChoice } from "../SupplierAdminPOSTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { enqueueSnackbar } from "notistack"
import { Accordion, AccordionDetails, AccordionSummary, Grid, MenuItem, Typography } from "@mui/material"
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { ConfirmModal } from "../../../../../../shared/components/dialogs/ConfirmModal"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { makeStyles } from "tss-react/mui"
import { useQueryClient } from "@tanstack/react-query"
import { objectHas } from "../../../../../../lib/lang"
import { ExternalLink } from "../../../../../../shared/components/links/ExternalLink"
import { AddIndustryCodeDialog } from "./AddIndustryCodeDialog"
import { InfoBox } from "../../../../../profile/components/InfoBox"

const ADYEN_INDUSTRY_CODES_URL =
    "https://docs.adyen.com/platforms/verification-requirements/reference-additional-products/#list-industry-codes"

const useStyles = makeStyles()((theme) => ({
    accordion: {
        borderRadius: theme.spacing(1.5),
        // Must override this too
        "&:last-of-type": {
            borderBottomLeftRadius: theme.spacing(1.5),
            borderBottomRightRadius: theme.spacing(1.5),
        },
    },
    accordionSummary: {
        paddingLeft: "2.5rem",
        paddingRight: "2.5rem",
    },
    accordionDetails: {
        paddingLeft: "2.5rem",
        paddingRight: "2.5rem",
        paddingBottom: "20px", // NOTE: Matches default top spacing in summary
    },
    contentGrid: {
        gap: "1rem",
    },
    tableContainer: {
        maxHeight: "100%",
    },
    tableHeadCell: {
        border: "none",
        padding: "0.75rem 1.5rem",
        color: theme.palette.tailwind.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: theme.palette.tailwind.slate[50],
    },
    topToolbar: {
        display: "none",
    },
    actionWrap: {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.8rem",
        padding: "0.8rem",
    },
    linkText: {
        color: "#2196F3",
        cursor: "pointer",
    },
}))

type ConfirmModalState = Readonly<{
    industryCodeId: string
    industryCode: string
}>

function selectIndustryCodes(data: FetchIndustryCodesResultResponse) {
    return data.industryCodes
}

type Props = Readonly<{
    agreementId: number
}>

export function AcquirerIndustryCodes({ agreementId }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { supplierId } = useCurrentSupplier()
    const queryClient = useQueryClient()

    const [addingCode, setAddingCode] = useState(false)
    const [confirmModalState, setConfirmModalState] = useState<ConfirmModalState>()

    const industryCodesResult = useApiQuery(fetchIndustryCodesResult, {
        queryName: POSQuery.INDUSTRY_CODES_DETAILS,
        dependencies: {
            supplierId,
        },
        select: selectIndustryCodes,
    })

    const { mutateAsync: callCreateIndustryCode } = useApiMutation(createIndustryCode, {
        async onMutate(params) {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: [POSQuery.INDUSTRY_CODES_DETAILS] })

            // Snapshot the previous value
            const prevIndustryCodes = queryClient.getQueryData([POSQuery.INDUSTRY_CODES_DETAILS])

            // Optimistically update to the new value
            queryClient.setQueryData<FetchIndustryCodesResultResponse>([POSQuery.INDUSTRY_CODES_DETAILS], (old) => {
                const addedCode: IndustryCode = {
                    id: "",
                    code: params.industryCode.code,
                    description: "",
                    refId: "",
                }

                const oldCodes = old?.industryCodes ?? []
                const newCodes = [...oldCodes, addedCode]
                const newResponse: FetchIndustryCodesResultResponse = {
                    industryCodes: newCodes,
                }

                return newResponse
            })

            // Return a context object with the snapshotted value
            return { prevIndustryCodes }
        },
        onError(_params, _error, context) {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (objectHas("prevIndustryCodes", context)) {
                queryClient.setQueryData([POSQuery.INDUSTRY_CODES_DETAILS], context.prevIndustryCodes)
            }
            return Promise.resolve()
        },
        async onSettled(_params, _data, _error, context) {
            // Always refetch after error or success
            await context.queryClient.invalidateQueries({ queryKey: [POSQuery.INDUSTRY_CODES_DETAILS] })
        },
    })

    const { mutate: callDeleteIndustryCodeItem } = useApiMutation(deleteIndustryCodeItem, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [POSQuery.INDUSTRY_CODES_DETAILS] })

            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(industryCodesResult)

    const columnsIndustryCodes = useMemo<MRT_ColumnDef<IndustryCode>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-acquiring-industry-codes-table-industry-code-column"),
                accessorKey: "code",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-acquiring-industry-codes-table-description-column"),
                accessorKey: "description",
            }),
        ],
        [l10n, columnFactory],
    )

    const handleAddClick = useCallback(() => {
        setAddingCode(true)
    }, [])

    const handleAddNewCodes = useCallback((pickedCodes: IndustryCodeChoice[]) => {
        setAddingCode(false)
    }, [])

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState?.industryCodeId != null) {
            callDeleteIndustryCodeItem({ industryCodeId: confirmModalState.industryCodeId, supplierId: supplierId })
        }
    }, [confirmModalState?.industryCodeId, callDeleteIndustryCodeItem])

    return (
        <>
            <Accordion defaultExpanded className={classes.accordion}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    className={classes.accordionSummary}
                >
                    <LocalizedStrict id="supplier-acquiring-industry-codes-accordion-industry-codes-title">
                        <Typography variant="body1" fontWeight={600}>
                            Industry codes
                        </Typography>
                    </LocalizedStrict>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                    <Grid container direction="column" className={classes.contentGrid}>
                        <LocalizedStrict
                            id="supplier-acquiring-industry-codes-accordion-industry-codes-info-box"
                            attrs={{ description: true }}
                        >
                            <InfoBox description="If you apply for a canteen, select industry code 722514 - cafeterias, grill buffets." />
                        </LocalizedStrict>
                        <ApiQueryTable
                            columns={columnsIndustryCodes}
                            enableClickToCopy={false}
                            enableColumnOrdering
                            enableStickyHeader
                            enableDensityToggle={false}
                            initialState={{ density: "spacious" }}
                            enableHiding={false}
                            enableRowActions={false} // TODO: Enable row actions when delete industry code has been implemented in API
                            enableColumnActions={false}
                            enableGrouping
                            enableBottomToolbar={false}
                            enablePagination={false}
                            muiTableContainerProps={{ className: classes.tableContainer }}
                            muiTablePaperProps={{ elevation: 0 }}
                            muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                            muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                            muiTopToolbarProps={{ className: classes.topToolbar }}
                            positionActionsColumn="last"
                            enableColumnDragging={false}
                            renderRowActionMenuItems={({ row, closeMenu }) => [
                                <MenuItem
                                    onClick={() => {
                                        setConfirmModalState({
                                            industryCodeId: row.original.id,
                                            industryCode: `${row.original.code}`,
                                        })
                                        closeMenu()
                                    }}
                                    key={`delete-${row.original.id}`}
                                >
                                    <LocalizedStrict id="supplier-acquiring-industry-codes-table-remove-action">
                                        <span>Remove</span>
                                    </LocalizedStrict>
                                </MenuItem>,
                            ]}
                            enableToolbarInternalActions={false}
                        />
                        <Grid item>
                            <LocalizedStrict id="supplier-acquiring-industry-codes-add-industry-code">
                                <StandardButton size="medium" fullWidth={false} color="blue" onClick={handleAddClick}>
                                    Add new industry code
                                </StandardButton>
                            </LocalizedStrict>
                        </Grid>
                        <LocalizedStrict
                            id="supplier-acquiring-industry-codes-info-text"
                            elems={{
                                industryCodesLink: (
                                    <ExternalLink
                                        href={ADYEN_INDUSTRY_CODES_URL}
                                        className={classes.linkText}
                                    ></ExternalLink>
                                ),
                            }}
                        >
                            <Typography variant="caption">
                                The industry code describes what kind of products you must sell in your shops. To sell
                                new kind of products you must apply for an approval at the Adyen platform. See all{" "}
                                <ExternalLink href={ADYEN_INDUSTRY_CODES_URL} className={classes.linkText}>
                                    industry codes
                                </ExternalLink>
                                .
                            </Typography>
                        </LocalizedStrict>
                    </Grid>
                </AccordionDetails>
            </Accordion>
            {addingCode && (
                <AddIndustryCodeDialog
                    agreementId={agreementId}
                    open={addingCode}
                    onClose={() => setAddingCode(false)}
                    onSave={handleAddNewCodes}
                />
            )}
            {confirmModalState && (
                <LocalizedStrict
                    id="supplier-acquiring-industry-codes-confirm-delete-dialog"
                    vars={{ industryCode: confirmModalState.industryCode }}
                    attrs={{ title: true, message: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        title="Remove industry code"
                        message={`Do you want to remove the industry code ${confirmModalState.industryCode}?`}
                        buttonLabelSubmit="Remove"
                        handleClick={handleDeleteClick}
                        open={confirmModalState !== undefined}
                        onClose={() => setConfirmModalState(undefined)}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
